/**
 * File: useBooksQueries.ts
 * Path: apps/admin/src/shared/hooks/useBooksQueries.ts
 * Description: Which set of books is open (the company's or your own), and
 * React Query wrappers over the accounting repository. Every change goes
 * through useBooksCommand, which refreshes that profile's data afterwards.
 */

import { useQuery } from "@tanstack/react-query";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import {
  ACCOUNTING_COLLECTIONS,
  COMPANY_PROFILE_ID,
  isBooksOwner,
  personalProfileId,
  type ProfileKind,
} from "@idk/config";
import { useAuth } from "@/modules/auth/components/AuthProvider";
import { accountingRepo } from "../services/accountingService";

interface BooksChoice {
  kind: ProfileKind;
  setKind: (kind: ProfileKind) => void;
}

/** Remembered per browser, so the books you last had open stay open. */
export const useBooksChoice = create<BooksChoice>()(
  persist(
    (set) => ({
      kind: "company",
      setKind: (kind) => set({ kind }),
    }),
    { name: "idk-admin-books", version: 1 },
  ),
);

export const booksKey = (profileId: string) => [ACCOUNTING_COLLECTIONS.profiles, profileId] as const;

/** True when the signed-in login owns the personal books (Liam's alone). */
export const useIsBooksOwner = () => {
  const { user } = useAuth();
  return isBooksOwner(user?.email);
};

/** The books to show: personal only for their owner, the company's for everyone else. */
export const useBooksKind = (): ProfileKind => {
  const chosen = useBooksChoice((s) => s.kind);
  return useIsBooksOwner() ? chosen : "company";
};

/** The open books: which ones, their id, and the profile (null: not set up). */
export const useOpenBooks = () => {
  const { user } = useAuth();
  const kind = useBooksKind();
  const setKind = useBooksChoice((s) => s.setKind);
  const uid = user?.uid ?? "";
  const profileId = kind === "company" ? COMPANY_PROFILE_ID : personalProfileId(uid);
  const profile = useQuery({
    queryKey: [...booksKey(profileId), "profile"],
    queryFn: () => accountingRepo.getProfile(profileId),
    enabled: kind === "company" || uid !== "",
  });
  return { kind, setKind, profileId, profile };
};

export const useAccounts = (profileId: string, enabled = true) =>
  useQuery({
    queryKey: [...booksKey(profileId), "accounts"],
    queryFn: () => accountingRepo.listAccounts(profileId),
    enabled,
  });

export const useJournals = (profileId: string, enabled = true) =>
  useQuery({
    queryKey: [...booksKey(profileId), "journals"],
    queryFn: () => accountingRepo.listJournals(profileId),
    enabled,
  });

export const useBankLines = (profileId: string, enabled = true) =>
  useQuery({
    queryKey: [...booksKey(profileId), "bankLines"],
    queryFn: () => accountingRepo.listBankLines(profileId),
    enabled,
  });

export const useBankStatements = (profileId: string, enabled = true) =>
  useQuery({
    queryKey: [...booksKey(profileId), "bankStatements"],
    queryFn: () => accountingRepo.listBankStatements(profileId),
    enabled,
  });

/** Both rule lists come from one cached read of the rules collection. */
const rulesQuery = (profileId: string, enabled: boolean) => ({
  queryKey: [...booksKey(profileId), "rules"],
  queryFn: () => accountingRepo.listRules(profileId),
  enabled,
});

export const useBankRules = (profileId: string, enabled = true) =>
  useQuery({ ...rulesQuery(profileId, enabled), select: (rules) => rules.learned });

export const useCustomBankRules = (profileId: string, enabled = true) =>
  useQuery({ ...rulesQuery(profileId, enabled), select: (rules) => rules.custom });

export const useReconciliations = (profileId: string, enabled = true) =>
  useQuery({
    queryKey: [...booksKey(profileId), "reconciliations"],
    queryFn: () => accountingRepo.listReconciliations(profileId),
    enabled,
  });

export const useTaxYear = (profileId: string, yearEnd: string, enabled = true) =>
  useQuery({
    queryKey: [...booksKey(profileId), "taxYear", yearEnd],
    queryFn: () => accountingRepo.getTaxYear(profileId, yearEnd),
    enabled,
  });

export const useTaxRecords = (profileId: string, enabled = true) =>
  useQuery({
    queryKey: [...booksKey(profileId), "taxRecords"],
    queryFn: () => accountingRepo.getTaxRecords(profileId),
    enabled,
  });

export const useFilings = (profileId: string, enabled = true) =>
  useQuery({
    queryKey: [...booksKey(profileId), "filings"],
    queryFn: () => accountingRepo.getFilings(profileId),
    enabled,
  });

export const useTaxYears = (profileId: string, enabled = true) =>
  useQuery({
    queryKey: [...booksKey(profileId), "taxYears"],
    queryFn: () => accountingRepo.listTaxYears(profileId),
    enabled,
  });

export const useCompanyDetails = (enabled = true) =>
  useQuery({
    queryKey: [...booksKey(COMPANY_PROFILE_ID), "cipc"],
    queryFn: () => accountingRepo.getCompanyDetails(),
    enabled,
  });

export const useBoDeclarations = (enabled = true) =>
  useQuery({
    queryKey: [...booksKey(COMPANY_PROFILE_ID), "beneficialOwnership"],
    queryFn: () => accountingRepo.listBoDeclarations(),
    enabled,
  });

export const useBooksEvents = (profileId: string, enabled = true) =>
  useQuery({
    queryKey: [...booksKey(profileId), "events"],
    queryFn: () => accountingRepo.listEvents(profileId),
    enabled,
  });
